import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import {
  catchError,
  concatMap,
  exhaustMap,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  tap,
  timer,
  withLatestFrom,
} from 'rxjs';

import * as fromGenerated from '../../_generated';
import { AdminGroupsActions } from './admin-groups.actions';
import {
  adminGroupsFeature,
  DUPLICATE_NAME_ERROR,
} from './admin-groups.reducer';

@Injectable()
export class AdminGroupsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly adminService = inject(fromGenerated.AdminService);
  private readonly toastrService = inject(ToastrService);
  private readonly translateService = inject(TranslateService);

  loadGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminGroupsActions.loadGroups),
      switchMap(() =>
        // timer(1000) keeps the spinner up at least 1s to avoid a flash.
        forkJoin([
          this.adminService.adminGroupsControllerListV1(),
          timer(1000),
        ]).pipe(
          map(([groups]) => AdminGroupsActions.loadGroupsSuccess({ groups })),
          catchError((error: Error) =>
            of(AdminGroupsActions.loadGroupsFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  loadGroupsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminGroupsActions.loadGroupsFailure),
        tap(() => this.toastError('ADMIN.GROUPS.TOAST.LOAD_ERROR')),
      ),
    { dispatch: false },
  );

  // Members for the edit modal are fetched fresh on every open (no cache).
  loadGroupMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminGroupsActions.openEditModal),
      switchMap(({ group }) =>
        this.adminService
          .adminGroupsControllerGetDropdownPayloadV1(
            group.id,
            this.currentLocale(),
          )
          .pipe(
            map((payload) =>
              AdminGroupsActions.loadGroupMembersSuccess({
                members: payload.users.items,
              }),
            ),
            catchError((error: Error) =>
              of(
                AdminGroupsActions.loadGroupMembersFailure({
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadGroupMembersFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminGroupsActions.loadGroupMembersFailure),
        tap(() => this.toastError('ADMIN.GROUPS.TOAST.MEMBERS_LOAD_ERROR')),
      ),
    { dispatch: false },
  );

  // Lazily fetch a row's expand payload when a count-cell dropdown opens. The
  // endpoint returns users + teammates + skills together, so one fetch fills
  // all three dropdowns for that row. A failure leaves the dropdown empty.
  loadDropdownPayload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminGroupsActions.loadDropdownPayload),
      concatMap(({ groupId }) =>
        this.adminService
          .adminGroupsControllerGetDropdownPayloadV1(
            groupId,
            this.currentLocale(),
          )
          .pipe(
            map((payload) =>
              AdminGroupsActions.loadDropdownPayloadSuccess({
                groupId,
                payload,
              }),
            ),
            catchError((error: Error) =>
              of(
                AdminGroupsActions.loadDropdownPayloadFailure({
                  groupId,
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  createGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminGroupsActions.createGroup),
      exhaustMap(({ name, memberIds }) =>
        this.adminService.adminGroupsControllerCreateV1({ name }).pipe(
          // T5 has no single { name, memberIds } endpoint — add members after.
          concatMap((group) =>
            memberIds.length === 0
              ? of(AdminGroupsActions.createGroupSuccess({ group }))
              : this.addMembersToNewGroup$(group, memberIds),
          ),
          // Only the create call itself reaches here; the member-add error is
          // already handled inside addMembersToNewGroup$.
          catchError((error: HttpErrorResponse) =>
            of(
              AdminGroupsActions.createGroupFailure({
                error:
                  error.status === 409 ? DUPLICATE_NAME_ERROR : error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  /**
   * Add the staged members to a freshly-created group, then report success
   * with server-authoritative counts. If the member-add fails the group still
   * exists, so we land the row and warn rather than reporting a create failure
   * (which would make a retry collide with the now-existing name).
   */
  private addMembersToNewGroup$(
    group: fromGenerated.GroupView,
    memberIds: string[],
  ): Observable<Action> {
    return this.adminService
      .adminGroupsControllerUpdateMembersV1(group.id, { add: memberIds })
      .pipe(
        concatMap(() => this.createSuccessWithCounts$(group)),
        catchError(() =>
          of(AdminGroupsActions.createGroupMembersFailed({ group })),
        ),
      );
  }

  /**
   * updateMembers returns void, so refetch the row's payload for true counts.
   * A count-refetch failure doesn't undo the add — keep the row without the
   * refreshed count (it self-heals on the next reload).
   */
  private createSuccessWithCounts$(
    group: fromGenerated.GroupView,
  ): Observable<Action> {
    return this.adminService
      .adminGroupsControllerGetDropdownPayloadV1(group.id, this.currentLocale())
      .pipe(
        map((payload) =>
          AdminGroupsActions.createGroupSuccess({
            group: this.withServerCounts(group, payload),
          }),
        ),
        catchError(() => of(AdminGroupsActions.createGroupSuccess({ group }))),
      );
  }

  createGroupMembersFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminGroupsActions.createGroupMembersFailed),
        tap(() =>
          this.toastWarning('ADMIN.GROUPS.TOAST.CREATE_MEMBERS_PARTIAL'),
        ),
      ),
    { dispatch: false },
  );

  createGroupSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminGroupsActions.createGroupSuccess),
        tap(() => this.toastSuccess('ADMIN.GROUPS.TOAST.CREATE_SUCCESS')),
      ),
    { dispatch: false },
  );

  createGroupFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminGroupsActions.createGroupFailure),
        // Duplicate-name is shown inline in the modal, not as a toast.
        filter(({ error }) => error !== DUPLICATE_NAME_ERROR),
        tap(() => this.toastError('ADMIN.GROUPS.TOAST.CREATE_ERROR')),
      ),
    { dispatch: false },
  );

  updateGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminGroupsActions.updateGroup),
      withLatestFrom(this.store.select(adminGroupsFeature.selectEntities)),
      exhaustMap(([{ groupId, name, addMembers, removeMembers }, entities]) => {
        const current = entities[groupId];
        // The name update returns the authoritative GroupView; member changes
        // return void, so we refetch the row's payload for true counts.
        // Annotate as a single Observable<...|null> so the ternary doesn't
        // become a union of observables (which breaks `.pipe()` resolution).
        const nameUpdate$: Observable<fromGenerated.GroupView | null> =
          name !== undefined
            ? this.adminService.adminGroupsControllerUpdateV1(groupId, { name })
            : of(null);
        // Build the DTO with only the present keys (exactOptionalPropertyTypes
        // rejects explicit `undefined` on optional properties).
        const membersDto: fromGenerated.UpdateGroupMembersDto = {};
        if (addMembers.length > 0) {
          membersDto.add = addMembers;
        }
        if (removeMembers.length > 0) {
          membersDto.remove = removeMembers;
        }
        const membersChanged =
          addMembers.length > 0 || removeMembers.length > 0;
        const counts$ = this.applyMemberChanges$(
          groupId,
          membersChanged,
          membersDto,
        );

        return nameUpdate$.pipe(
          concatMap((named) =>
            counts$.pipe(
              map((payload) =>
                this.buildUpdateSuccess(current, named, payload),
              ),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              AdminGroupsActions.updateGroupFailure({
                error:
                  error.status === 409 ? DUPLICATE_NAME_ERROR : error.message,
              }),
            ),
          ),
        );
      }),
    ),
  );

  /**
   * Apply a member add/remove (if any) and return the server-authoritative
   * payload for count reconciliation. updateMembers returns void, so we refetch
   * the row's payload; a refetch failure falls back to `null` (keep base counts,
   * self-heals on reload) rather than failing the whole update.
   */
  private applyMemberChanges$(
    groupId: string,
    membersChanged: boolean,
    membersDto: fromGenerated.UpdateGroupMembersDto,
  ): Observable<fromGenerated.GroupDropdownPayloadView | null> {
    if (!membersChanged) {
      return of(null);
    }
    return this.adminService
      .adminGroupsControllerUpdateMembersV1(groupId, membersDto)
      .pipe(
        concatMap(() =>
          this.adminService
            .adminGroupsControllerGetDropdownPayloadV1(
              groupId,
              this.currentLocale(),
            )
            .pipe(catchError(() => of(null))),
        ),
      );
  }

  /**
   * Build the update-success action: prefer the server's GroupView (returned by
   * a name change) over the cached row, then layer server-true counts on top
   * when members changed.
   */
  private buildUpdateSuccess(
    current: fromGenerated.GroupView | undefined,
    named: fromGenerated.GroupView | null,
    payload: fromGenerated.GroupDropdownPayloadView | null,
  ): Action {
    const base = named ?? current;
    if (!base) {
      return AdminGroupsActions.updateGroupFailure({
        error: 'group-not-found',
      });
    }
    const group = payload ? this.withServerCounts(base, payload) : base;
    return AdminGroupsActions.updateGroupSuccess({ group });
  }

  updateGroupSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminGroupsActions.updateGroupSuccess),
        tap(() => this.toastSuccess('ADMIN.GROUPS.TOAST.UPDATE_SUCCESS')),
      ),
    { dispatch: false },
  );

  updateGroupFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminGroupsActions.updateGroupFailure),
        filter(({ error }) => error !== DUPLICATE_NAME_ERROR),
        tap(() => this.toastError('ADMIN.GROUPS.TOAST.UPDATE_ERROR')),
      ),
    { dispatch: false },
  );

  deleteGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminGroupsActions.deleteGroup),
      exhaustMap(({ groupId }) =>
        this.adminService.adminGroupsControllerDeleteV1(groupId).pipe(
          map(() => AdminGroupsActions.deleteGroupSuccess({ groupId })),
          catchError((error: Error) =>
            of(AdminGroupsActions.deleteGroupFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  deleteGroupSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminGroupsActions.deleteGroupSuccess),
        tap(() => this.toastSuccess('ADMIN.GROUPS.TOAST.DELETE_SUCCESS')),
      ),
    { dispatch: false },
  );

  deleteGroupFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminGroupsActions.deleteGroupFailure),
        tap(() => this.toastError('ADMIN.GROUPS.TOAST.DELETE_ERROR')),
      ),
    { dispatch: false },
  );

  /**
   * Overlay server-authoritative counts (from a dropdown-payload refetch) onto
   * a GroupView, so the row reflects the real membership rather than a
   * client-side guess. Teammate/skill counts come along for free.
   */
  private withServerCounts(
    group: fromGenerated.GroupView,
    payload: fromGenerated.GroupDropdownPayloadView,
  ): fromGenerated.GroupView {
    return {
      ...group,
      memberCount: payload.users.count,
      teammateCount: payload.teammates.count,
      skillCount: payload.skills.count,
    };
  }

  /**
   * Admin reads resolve localized teammate/skill names server-side, so every
   * request that accepts a locale gets the viewer's current UI language.
   */
  private currentLocale(): 'en' | 'fr' {
    // The app registers only en + fr; narrow at runtime (default en) rather than
    // casting, so an unexpected lang can't reach the API as an invalid locale.
    return this.translateService.getCurrentLang() === 'fr' ? 'fr' : 'en';
  }

  private toastSuccess(key: string): void {
    this.toastrService.success(
      this.translateService.instant(key) as string,
      undefined,
      { closeButton: true },
    );
  }

  private toastError(key: string): void {
    // Errors persist (no auto-dismiss) so the user has time to read them;
    // success/info/warn keep the global 4s timeout.
    this.toastrService.error(
      this.translateService.instant(key) as string,
      undefined,
      { closeButton: true, disableTimeOut: true },
    );
  }

  private toastWarning(key: string): void {
    this.toastrService.warning(
      this.translateService.instant(key) as string,
      undefined,
      { closeButton: true },
    );
  }
}
